import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useScopeStore } from '@/stores/scopeStore.ts';
import { LineSizeGenericView, PipingClassGenericView, PipingClassSizeGenericView } from '@/apps/STID_Pipe/pipingClass.ts';
import { genericViewQueryAsText, getCodeSets } from '@/shared/helpers/api.ts';
import { http } from '@/shared/httpWrapper';
import _ from 'lodash';
import { usePreselectPipeClassStore } from '@/stores/preselectPipeClassStore.ts';

class Loading {
    pipingClasses: boolean;
    pipingClassSizes: boolean;
    lineSizes: boolean;
    lineSizeCodeSetName: boolean;
    pipingClassSizeCodeSetName: boolean;
    pipingClassCodeSetName: boolean;

    constructor() {
        this.pipingClasses = false;
        this.pipingClassSizes = false;
        this.lineSizes = false;
        this.lineSizeCodeSetName = false;
        this.pipingClassSizeCodeSetName = false;
        this.pipingClassCodeSetName = false;
    }

    get any() {
        return this.pipingClasses || this.pipingClassSizes || this.lineSizes || this.pipingClassCodeSetName || this.pipingClassSizeCodeSetName || this.lineSizeCodeSetName;
    }
}

function parseStringColumnAsNumber(obj: Record<string, number | string | boolean | null>, columnName: string) {
    const columnValue = obj[columnName];
    if (typeof columnValue === 'string') {
        return parseFloat(columnValue);
    }
    return 0.0;
}

export const usePipeClassStore = defineStore('pipeClass', () => {
    const preselect = usePreselectPipeClassStore();
    // pipeClass
    const pipingClasses = ref<PipingClassGenericView[]>([]);
    const selectedPipingClass = ref<PipingClassGenericView | null>(null);

    // pipeClassSize
    const pipingClassSizes = ref<PipingClassSizeGenericView[]>([]);
    const selectedPipingClassSize = ref<PipingClassSizeGenericView | null>(null);

    // lineSize
    const lineSizes = ref<LineSizeGenericView[]>([]);
    const selectedLineSize = ref<LineSizeGenericView | null>(null);

    const currentPipingClassCodeSetName = ref<string | null>(null);
    const currentLineSizeCodeSetName = ref<string | null>(null);
    const currentPipingClassSizeCodeSetName = ref<string | null>(null);

    const loading = ref<Loading>(new Loading());

    const scopeStore = useScopeStore();

    async function init() {
        if (scopeStore.getSelectedScope()) {
            await loadForScope();
        }
    }

    function setPipingClass(id: number | null) {
        selectedPipingClass.value = pipingClasses.value.find((pc) => pc.Id === id) ?? null;
        setPipingClassSize(null);
    }

    function setPipingClassSize(id: number | null) {
        selectedPipingClassSize.value = pipingClassSizes.value.find((pcs) => pcs.Id === id) ?? null;
    }

    function setLineSize(id: number | null) {
        selectedLineSize.value = lineSizes.value.find((ls) => ls.Id === id) ?? null;
    }

    function addPipingClassSize(size: PipingClassSizeGenericView) {
        pipingClassSizes.value.push(size);
    }

    function updateFacilityScope(value: string) {
        scopeStore.updateSelectedScope(value);
    }

    function setPipingClasses(items: PipingClassGenericView[], keepSelected: boolean = false) {
        const currentSelection = selectedPipingClass.value?.Id ?? -1;
        pipingClasses.value = items;
        if (keepSelected) {
            selectedPipingClass.value = pipingClasses.value.find(pc => pc.Id === currentSelection) ?? null;
        }
    }

    function loadPreselectedClass() {
        setPipingClass(preselect.pipingClassId);
        preselect.setPipingClass(null);
    }

    function loadPreselectedClassSize() {
        setPipingClassSize(preselect.pipingClassSizeId);
        preselect.setPipingClassSize(null);
    }

    function loadPreselectedLineSize() {
        setLineSize(preselect.lineSizeId);
        preselect.setLineSize(null);
    }

    function setPipingClassSizes(items: PipingClassSizeGenericView[], keepSelected: boolean = false) {
        const currentSelection = selectedPipingClassSize.value?.Id ?? -1;
        pipingClassSizes.value = items;
        if (keepSelected) {
            selectedPipingClassSize.value = pipingClassSizes.value.find(pcs => pcs.Id === currentSelection) ?? null;
        }
    }

    function setLineSizes(items: LineSizeGenericView[], keepSelected: boolean = false) {
        const currentSelection = selectedLineSize.value?.Id ?? -1;
        lineSizes.value = items;
        if (keepSelected) {
            selectedLineSize.value = lineSizes.value.find(ls => ls.Id === currentSelection) ?? null;
        }
    }

    async function loadForScope() {
        await Promise.all([
            loadPipingClasses(false),
            loadLineSizes(false),
            loadPipingClassCodeSetName(),
            loadLineSizeCodeSetName(),
            loadPipingClassSizeCodeSetName()
        ]);
    }

    async function loadPipingClassSizes(keepSelected: boolean, returnNew: boolean = false): Promise<PipingClassSizeGenericView[] | undefined> {
        loading.value.pipingClassSizes = true;
        const scope = scopeStore.getSelectedScope();
        if (scope === null || !selectedPipingClass.value) {
            setPipingClassSizes([]);
            loading.value.pipingClassSizes = false;
            return returnNew ? [] : undefined;
        }
        const response = await genericViewQueryAsText(
            http,
            'FROM PipingClassSize WHERE Scope = "@Scope" AND PipingClass = "@PipingClass"',
            [{ name: '@Scope', value: scope }, {
                name: '@PipingClass',
                value: selectedPipingClass.value?.Identity
            }]
        );

        const responseData = response.data as PipingClassSizeGenericView[];

        const existingIdentities = pipingClassSizes.value.map(x => x.Identity);

        setPipingClassSizes(_.sortBy(responseData, e => parseStringColumnAsNumber(e, 'LineSize')), keepSelected);

        if (!keepSelected) {
            loadPreselectedClassSize();
        }
        loading.value.pipingClassSizes = false;
        if (returnNew) {
            return pipingClassSizes.value.filter(p => !existingIdentities.includes(p.Identity));
        }
    }

    async function loadPipingClassCodeSetName() {
        loading.value.pipingClassCodeSetName = true;
        currentPipingClassCodeSetName.value = await fetchCodeSetName('PipingClass');
        loading.value.pipingClassCodeSetName = false;
    }

    async function loadLineSizeCodeSetName() {
        loading.value.lineSizeCodeSetName = true;
        currentLineSizeCodeSetName.value = await fetchCodeSetName('LineSize');
        loading.value.lineSizeCodeSetName = false;
    }

    async function loadPipingClassSizeCodeSetName() {
        loading.value.pipingClassSizeCodeSetName = true;
        currentPipingClassSizeCodeSetName.value = await fetchCodeSetName('PipingClassSize');
        loading.value.pipingClassSizeCodeSetName = false;
    }

    async function fetchCodeSetName(libraryName: string) {
        const scope = scopeStore.getSelectedScope();
        if (scope === null) {
            return null;
        }
        const res = await getCodeSets(http, libraryName, scope);
        return res.length === 1 ? res[0].name : null;
    }

    watch(selectedPipingClass, async () => {
        await loadPipingClassSizes(false);
    });

    /**
     * Loads line sizes for the current scope into the store
     * @param returnNew Return a list of entries that were not present before.
     */
    async function loadLineSizes(keepSelected: boolean, returnNew: boolean = false): Promise<LineSizeGenericView[] | undefined> {
        loading.value.lineSizes = true;
        const scope = scopeStore.getSelectedScope();
        if (scope === null) {
            setLineSizes([]);
            loading.value.lineSizes = false;
            return returnNew ? [] : undefined;
        }
        const response = await genericViewQueryAsText(
            http,
            'FROM LineSize WHERE Scope = "@Scope"',
            [{ name: '@Scope', value: scope }]
        );
        const responseData = response.data as LineSizeGenericView[];

        const existingIdentities = lineSizes.value.map(ls => ls.Identity);

        setLineSizes(_.sortBy(responseData, e => parseStringColumnAsNumber(e, 'Name')), keepSelected);
        if (!keepSelected) {
            loadPreselectedLineSize();
        }

        loading.value.lineSizes = false;

        if (returnNew) {
            return lineSizes.value.filter(ls => !existingIdentities.includes(ls.Identity));
        }
    }

    async function loadPipingClasses(keepSelected: boolean, returnNew: boolean = false) {
        loading.value.pipingClasses = true;
        const scope = scopeStore.getSelectedScope();
        if (scope === null) {
            setPipingClasses([]);
            loading.value.pipingClasses = false;
            return returnNew ? [] : undefined;
        }
        const response = await genericViewQueryAsText(
            http,
            'FROM PipingClass WHERE Scope = "@Scope" ORDER BY Name',
            [{ name: '@Scope', value: scope }]
        );
        const existingIdentities = pipingClasses.value.map(pc => pc.Identity);

        setPipingClasses(response.data as PipingClassGenericView[], keepSelected);

        if (!keepSelected) {
            loadPreselectedClass();
        }

        loading.value.pipingClasses = false;

        if (returnNew) {
            return pipingClasses.value.filter(pc => !existingIdentities.includes(pc.Identity));
        }
    }

    const selectedScope = computed(() => {
        return scopeStore.selectedScope;
    });

    return {
        addPipingClassSize,
        lineSizes,
        loading,
        loadPipingClasses,
        loadPipingClassSizes,
        loadLineSizes,
        loadPreselectedClass,
        loadPreselectedClassSize,
        loadPreselectedLineSize,
        pipingClassSizes,
        pipingClasses,
        selectedPipingClass,
        selectedPipingClassSize,
        selectedLineSize,
        setLineSize,
        setPipingClass,
        setPipingClassSize,
        selectedScope,
        currentLineSizeCodeSetName,
        currentPipingClassCodeSetName,
        currentPipingClassSizeCodeSetName,
        init,
        updateFacilityScope
    };
});
